import React, { useMemo, useState } from "react";
import { IconButton, Label, Dropdown } from "@vibe/core";
import { Email, Retry } from "@vibe/icons";
import useFetchUserStatus from "../../hooks/api/useFetchUserStatus";
import PageLoader from "../PageLoader";
import { Pagination } from "@mui/material";
import useBoardAndItems from "../../hooks/context/useBoardAndItems";
import { MondayApiQueryInstance } from "../../services/monday/monday-service-ts";
import mondaySdk from "monday-sdk-js";
import logger from "../../lib/logger";
import { UserAvatar } from "../ui/UserAvatar";
import { Search } from "@vibe/core";

const monday = mondaySdk();
const backend_uri = process.env.REACT_APP_URI;

const UsersGrid = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { users, isLoading, error, fetchAllUsers } = useFetchUserStatus(500, searchTerm); // Fetch all users
  const { currentBoard } = useBoardAndItems();

  const handleSearch = (event: string) => {
    setSearchTerm(event);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState<any>({
    value: "all",
    label: "All",
  }); // State for filter
  const usersPerPage = 10;

  // Apply filter based on status
  const filteredUsers = users?.filter((user: any) => {
    if (filterStatus?.value === "all") return true;
    return filterStatus?.value === "authorized" ? user?.status : !user?.status;
  });

  const filterOptions = useMemo(
    () => [
      {
        value: "all",
        label: "All",
      },
      {
        value: "authorized",
        label: "Authorized",
      },
      {
        value: "unauthorized",
        label: "Unauthorized",
      },
    ],
    [],
  );

  // Client-side pagination logic
  const startIndex = (currentPage - 1) * usersPerPage;
  const visibleUsers = filteredUsers?.slice(startIndex, startIndex + usersPerPage);
  const totalPages = Math.ceil((filteredUsers?.length || 0) / usersPerPage);

  const handleSendAuthNotification = async (userId?: number) => {
    try {
      if (!userId || !currentBoard) return;

      await MondayApiQueryInstance.sendNotification(
        currentBoard,
        userId,
        `Hi there! Please authorize the <a rel='norefferer' target='_blank' href='${backend_uri}/oauth/authorize?source=newtab'>Same Item Multiple Boards</a> app to keep your updates synced across other boards. It only takes a moment—thank you!`,
      );
      monday.execute("notice", {
        message: `Authorization notification sent! `,
        type: "success",
        timeout: 10000,
      });
    } catch (error: any) {
      monday.execute("notice", {
        message: `${
          error?.data?.errorCode === "NotificationTargetUserUnauthorizedException" ||
          error?.data?.errors[0]?.extensions?.code === "NotificationTargetUserUnauthorizedException"
            ? "User does not have access to this board. Please send this message from another board."
            : `Failed to send notification: ${error.message}`
        }`,
        type: "error",
        timeout: 10000,
      });
      logger.error("Error Sending Authorization Message:", error);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setCurrentPage(newPage); // Update the current page for client-side pagination
  };

  const handleRetry = () => {
    // Retry fetching users
    fetchAllUsers("");
  };

  const handleFilterChange = (value: any) => {
    setFilterStatus(value); // Update filter status
    setCurrentPage(1); // Reset to the first page when filter changes
  };

  if (error) {
    return (
      <div className="flex flex-col gap-2 justify-center items-center mx-auto">
        <IconButton
          onClick={handleRetry}
          className="cursor-pointer"
          icon={Retry}
          size="small"
          kind="primary"
        />
        <span>An error occurred. Please try again.</span>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1">
        {/* Filter Dropdown */}
        <div className="flex flex-row justify-between mb-4">
          <div className="min-w-[240px]">
            <Search
              size="small"
              placeholder="Search Team Member"
              value={searchTerm}
              onChange={(event) => {
                handleSearch(event);
              }} // Simplified onChange
            />
          </div>
          <Dropdown
            multi={false}
            size="small"
            placeholder={"Filter"}
            options={filterOptions}
            value={filterStatus}
            clearable={false}
            defaultValue={[{ value: "all", label: "All" }]}
            onChange={(selectedOption: any) => {
              // Safely handle the selected option
              handleFilterChange(selectedOption);
            }}
            className="min-w-[240px]"
          />
        </div>

        {isLoading ? (
          <PageLoader screenHeight={false} minHeight={462} />
        ) : (
          <>
            {visibleUsers?.length !== 0 &&
              visibleUsers?.map((user: any) => (
                <div
                  key={user?.id}
                  className="grid grid-cols-[2fr_1fr] p-2 border border-solid border-t-[#E7E9EF] border-b-0 border-r-0 border-l-0 box-border"
                >
                  {/* user column */}
                  <div className="flex gap-3 items-center">
                    <UserAvatar
                      name={user?.name}
                      photoUrl={user?.photo_thumb}
                      size="md"
                      className="-mx-1 border-[#f5f6f8]"
                    />
                    <p className="text-sm">{user?.name}</p>
                  </div>

                  {/* Action/Status column */}
                  <div className="flex gap-3 justify-end items-center">
                    <Label
                      text={user?.status ? "Authorized" : "Unauthorized"}
                      color={user?.status ? Label.colors.POSITIVE : Label.colors.DARK}
                    />
                    <IconButton
                      tooltipContent="Send Authorization Notification"
                      icon={Email}
                      size="small"
                      disabled={user?.status ? true : false}
                      onClick={() => {
                        handleSendAuthNotification(user?.id);
                      }}
                    />
                  </div>
                </div>
              ))}
            <div className="flex justify-center mb-4">
              <Pagination
                page={currentPage}
                onChange={handlePageChange}
                count={totalPages} // Total pages based on filtered users
                color="primary"
                shape="rounded"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UsersGrid;
